import { useEffect, useState } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Cube, UsersThree } from "@phosphor-icons/react";

export default function AcceptInvite() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const navigate = useNavigate();
  const { refresh } = useAuth();
  const [invite, setInvite] = useState(null); // null=loading, false=invalid
  const [name, setName] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!token) { setInvite(false); return; }
    api.get(`/auth/invite/${token}`).then((r) => setInvite(r.data)).catch(() => setInvite(false));
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await api.post("/auth/accept-invite", { token, name, password });
      await refresh();
      navigate("/dashboard");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-8">
      <div className="w-full max-w-sm animate-fade-up">
        <div className="flex items-center gap-2 mb-8">
          <Cube size={26} weight="bold" />
          <span className="font-heading font-black text-lg tracking-tighter">BuildTrack</span>
        </div>

        {invite === null ? (
          <div className="label-caps animate-pulse">Checking invitation…</div>
        ) : invite === false ? (
          <div className="border border-destructive p-6" data-testid="invite-invalid">
            <h1 className="font-heading text-2xl font-black tracking-tighter mb-2">Invalid invitation</h1>
            <p className="text-sm text-muted-foreground mb-6">This invite link is invalid or has expired.</p>
            <Button asChild className="w-full rounded-none"><Link to="/register">Create a new account</Link></Button>
          </div>
        ) : (
          <form onSubmit={submit} data-testid="accept-invite-form">
            <div className="flex items-center gap-2 mb-2 text-muted-foreground">
              <UsersThree size={18} />
              <p className="label-caps">Team invitation</p>
            </div>
            <h1 className="font-heading text-3xl font-black tracking-tighter mb-2">Join {invite.team_name}</h1>
            <p className="text-sm text-muted-foreground mb-6">
              {invite.invited_by} invited <span className="font-medium text-foreground">{invite.email}</span>. Set up your account to accept.
            </p>

            {error && (
              <div data-testid="invite-error" className="mb-4 border border-destructive px-4 py-3 text-sm text-destructive">{error}</div>
            )}

            <div className="space-y-4">
              <div>
                <Label className="label-caps">Full name</Label>
                <Input value={name} onChange={(e) => setName(e.target.value)} required data-testid="invite-name-input" className="mt-2" placeholder="Your name" />
              </div>
              <div>
                <Label className="label-caps">Password</Label>
                <Input type="password" value={password} onChange={(e) => setPassword(e.target.value)} required minLength={6} data-testid="invite-password-input" className="mt-2" placeholder="Min 6 characters" />
              </div>
            </div>

            <Button type="submit" disabled={loading} data-testid="invite-submit-button" className="w-full mt-6 rounded-none h-11">
              {loading ? "Joining…" : "Accept & Join"}
            </Button>
          </form>
        )}
      </div>
    </div>
  );
}
